import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { DatePickerProps, DateRangePicker } from '@creditornot/cb-date-picker';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { $Values } from 'utility-types';

import { PredefinedDateRangeOptions, telemetryClickDatePickerInHomeInsightPage } from '../telemetry';
import { DateRange, Period } from '../types';
import { getDateRanges } from '../utils';

export type Props = {
  onChange: (dateRange: DateRange) => void;
  value: DateRange;
  locale?: DatePickerProps['locale'];
};

const DateRangeNamesToTelemetry: Record<
  Exclude<Period, Period.SEVEN_DAYS_AGO>,
  $Values<typeof PredefinedDateRangeOptions>
> = {
  [Period.TODAY]: PredefinedDateRangeOptions.DATE_PICKER_TODAY,
  [Period.YESTERDAY]: PredefinedDateRangeOptions.DATE_PICKER_YESTERDAY,
  [Period.LAST_WEEK]: PredefinedDateRangeOptions.DATE_PICKER_LAST_WEEK,
  [Period.THIS_MONTH]: PredefinedDateRangeOptions.DATE_PICKER_THIS_MONTH,
  [Period.LAST_28_DAYS]: PredefinedDateRangeOptions.DATE_PICKER_LAST_28_DAYS,
};

const Root = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 268px;
  padding: 8px;
  width: 100%;
`;

const Divider = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  width: 100%;
`;

const ButtonGroup = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DataDelayAlert = styled(Alert)``;

const prepareDateLengthChosen = (dateRange: DateRange) => {
  if (!dateRange.from || !dateRange.to) {
    return null;
  }

  const startDate = DateTime.fromJSDate(dateRange.from).toISODate();
  const endDate = DateTime.fromJSDate(dateRange.to).toISODate();
  // if only one day is selected, length should be 1
  const length =
    Math.round(
      DateTime.fromJSDate(dateRange.to).startOf('day').diff(DateTime.fromJSDate(dateRange.from).startOf('day'), 'days')
        .days,
    ) + 1;

  return {
    startDate,
    endDate,
    length,
  };
};

const getPeriodNameForTelemetry = (periodName?: DateRange['name']) => {
  if (periodName === Period.SEVEN_DAYS_AGO || !periodName) {
    return undefined;
  }

  return DateRangeNamesToTelemetry[periodName];
};

const executeTelemetryOnDateRange = (dateRange: DateRange) => {
  const targetContentDatePicker = getPeriodNameForTelemetry(dateRange.name);
  const dateLengthChosen = prepareDateLengthChosen(dateRange);

  if (dateLengthChosen) {
    telemetryClickDatePickerInHomeInsightPage({
      targetContentDatePicker,
      dateLengthChosen,
    });
  }
};

const FilterDateRangePicker = ({ onChange, value, locale }: Props) => {
  const {
    todayDateRange,
    yesterdayDateRange,
    lastWeekDateRange,
    thisMonthDateRange,
    last28daysDateRange,
    disabledDays,
  } = getDateRanges();

  const handleDateRangeChange = (dateRange: DateRange) => {
    onChange(dateRange);
    executeTelemetryOnDateRange(dateRange);
  };

  const shouldShowDataDelayNotice =
    value && value.from && value.to && DateTime.now().startOf('day') <= DateTime.fromJSDate(value.to);

  return (
    <Root>
      <DateRangePicker onChange={handleDateRangeChange} value={value} disabledDays={disabledDays} locale={locale} />
      <Divider />
      <ButtonGroup role="group">
        <Button stretch variant="secondary" size="small" onClick={() => handleDateRangeChange(todayDateRange)}>
          <FormattedMessage id="purchases.labels.today" />
        </Button>
        <Button stretch variant="secondary" size="small" onClick={() => handleDateRangeChange(yesterdayDateRange)}>
          <FormattedMessage id="purchases.labels.yesterday" />
        </Button>
        <Button stretch variant="secondary" size="small" onClick={() => handleDateRangeChange(lastWeekDateRange)}>
          <FormattedMessage id="purchases.labels.last-week" />
        </Button>
        <Button stretch variant="secondary" size="small" onClick={() => handleDateRangeChange(thisMonthDateRange)}>
          <FormattedMessage id="purchases.labels.this-month" />
        </Button>
        <Button stretch variant="secondary" size="small" onClick={() => handleDateRangeChange(last28daysDateRange)}>
          <FormattedMessage id="insights.date-picker.last-28" />
        </Button>
      </ButtonGroup>
      {shouldShowDataDelayNotice && (
        <DataDelayAlert variant="error">
          <FormattedMessage id="insights.data-delay" />
        </DataDelayAlert>
      )}
    </Root>
  );
};

export default FilterDateRangePicker;
